import { StrictMode } from "react";
import { createRoot, type Root } from "react-dom/client";

import {
  ensureContainer,
  ensureShadowRoot,
  resolveDesiredHost,
  ensureUsableHostOrFallback,
  ensurePortalContainer,
  ensureOdsStylesInShadow,
} from "@/core";
import { useWidgetStore } from "@/state";
import App from "@/App";
import { I18nextProvider } from "react-i18next";
import i18n from "@/i18n";
import { AppThemeProvider } from "@/providers";

import {
  waitForPublicStateReady,
  waitForTrigger,
  tryOpenMenu,
  installWidgetApi,
  onDomReady,
  eagerMountIfPossible,
  restoreHostDisplayIfForced,
  forceHostVisibleEl,
  shouldBlockOpen,
} from "@/bootstrap";
import { widgetReady } from "@/bootstrap/widgetReady";
import type { WidgetApi } from "@/types";

window.TelekomIdWidget = { widgetReady } as WidgetApi;

type Mounted = {
  hostEl: HTMLElement;
  shadow: ShadowRoot;
  reactRoot: Root;
  container: HTMLElement;
};

let mounted: Mounted | null = null;

const renderApp = (
  container: HTMLElement,
  shadowRoot: ShadowRoot,
  themeHost: HTMLElement,
) => {
  const root = createRoot(container);

  root.render(
    <StrictMode>
      <AppThemeProvider
        emotionContainer={shadowRoot}
        themeHost={themeHost}
      >
        <I18nextProvider i18n={i18n}>
          <App />
        </I18nextProvider>
      </AppThemeProvider>
    </StrictMode>,
  );

  return root;
};

const getLegacyAutoHost = (): HTMLElement | null => {
  const script = document.getElementById("md-login");
  if (!script) return null;

  return document.getElementById("telekomId-host") as HTMLElement | null;
};

export type MountArgs = { host?: string | HTMLElement };

let hostObserver: MutationObserver | null = null;

const DEFAULT_TRIGGER_ID = "kc-widget-avatar";

export const mount = async (args?: MountArgs) => {
  const explicitHost = resolveDesiredHost(args?.host);

  // unmount first to avoid React roots + Emotion styles piling up.
  if (mounted && !mounted.hostEl.isConnected) {
    unmount();
  }

  // re-host support
  if (mounted?.hostEl?.isConnected) {
    if (explicitHost && mounted.hostEl !== explicitHost) {
      unmount();
    } else {
      return;
    }
  }

  const scriptEl = document.getElementById("md-login");
  if (!scriptEl) throw new Error("[telekomId] Missing #md-login script tag");

  const legacyAutoHost = getLegacyAutoHost();
  const hostEl = ensureUsableHostOrFallback(explicitHost, legacyAutoHost);

  useWidgetStore.getState().setHostEl(hostEl);

  const shadow = ensureShadowRoot(hostEl);
  ensureOdsStylesInShadow(shadow);
  const container = ensureContainer(shadow);

  const portal = ensurePortalContainer(shadow);
  useWidgetStore.getState().setPortalContainer(portal);

  const reactRoot = renderApp(container, shadow, hostEl);

  mounted = { hostEl, shadow, reactRoot, container };

  hostObserver?.disconnect();
  hostObserver = new MutationObserver(() => {
    if (!mounted) return;

    // host removed => remount
    if (!mounted.hostEl.isConnected) {
      const next = document.getElementById(DEFAULT_TRIGGER_ID);

      // Try to remount into the re-created host; fallback to legacy auto host
      void mount({ host: next ?? undefined }).catch(() => {});
    }
  });

  hostObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
};

export const unmount = () => {
  if (!mounted) return;

  restoreHostDisplayIfForced(mounted.hostEl);

  mounted.reactRoot.unmount();

  const ui = useWidgetStore.getState();
  ui.setPortalContainer(null);
  ui.setMenuAnchorEl(null);
  ui.setTriggerEl(null);
  ui.setUiVisible(false);
  ui.setHostEl(null);

  hostObserver?.disconnect();
  hostObserver = null;

  mounted = null;
};

export const openWidget = async (host?: string | HTMLElement) => {
  if (shouldBlockOpen(250)) return;

  await mount(host ? { host } : undefined);

  const ui = useWidgetStore.getState();
  const hostEl = ui.hostEl ?? null;

  const isMenuOpen = Boolean(ui.menuAnchorEl);

  if (isMenuOpen) {
    ui.setMenuAnchorEl(null);

    requestAnimationFrame(() => ui.setUiVisible(false));
    if (hostEl) restoreHostDisplayIfForced(hostEl);

    return;
  }

  if (hostEl) forceHostVisibleEl(hostEl);

  ui.setUiVisible(true);

  await waitForPublicStateReady();
  await waitForTrigger({ allowManual: true });

  const st = ui.state;
  const actions = ui.actions;

  if (!st.loggedIn) {
    actions.loginFn();
    return;
  }

  tryOpenMenu();
};

installWidgetApi({ mount, unmount, openWidget });

onDomReady(() => {
  eagerMountIfPossible(mount);
});
